import { useMemo } from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import {
  Blockchain,
  PERSONA_CONTRACT_ABI,
  STAKING_CONTRACT_ABI,
} from '@repo/prsn-blockchain';
import { useMutation } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
  Address,
  formatEther,
  isAddressEqual,
  WaitForTransactionReceiptReturnType,
  zeroAddress,
} from 'viem';
import {
  useAccount,
  useChainId,
  useReadContract,
  useReadContracts,
  useWaitForTransactionReceipt,
  useWatchBlockNumber,
} from 'wagmi';

import {
  claim,
  hasApprovalForAllNfts,
  setApprovalForAllNfts,
  stakeOnBase,
  unstake,
} from '@web/features/staking/service';
import {
  getClaimedReward,
  READ_RECEIPT_TIMEOUT,
} from '@web/features/staking/utils';
import { env } from '@web/utils/env';
import { chains } from '@web/utils/rainbowkit/chains';

import { useCurrentCompletedUser, useCurrentUser } from '../user/service';

export function useBlockchain(): Blockchain {
  const chainId = useChainId();
  const blockchain = (Object.keys(chains) as Blockchain[]).find(
    (blockchain) => chains[blockchain].id === chainId,
  );
  if (!blockchain) {
    throw new Error(
      `${chainId} was configure at wagmi level but not in Persona codebase. Only compatible blockchains should be registered in wagmi configuration.`,
    );
  }
  return blockchain;
}

export function useDeposits({ enabled }: { enabled?: boolean } = {}) {
  const wallet = useAccount();
  const readDeposits = useReadContract({
    chainId: chains.base.id,
    address: env.base.STAKING_ADDRESS,
    abi: STAKING_CONTRACT_ABI,
    functionName: 'depositsOf',
    args: [wallet.address ?? zeroAddress],
    query: {
      enabled: enabled && wallet.status === 'connected',
    },
  });

  useWatchBlockNumber({
    onBlockNumber: (blockNumber) => {
      if (blockNumber % 3n === 0n) {
        readDeposits.refetch({ cancelRefetch: false });
      }
    },
    enabled: enabled && wallet.status === 'connected',
  });

  const depositsRecord = useMemo(() => {
    if (!readDeposits.data) return {};

    const [tokenIds, deposits] = readDeposits.data;

    return tokenIds.reduce(
      (acc, tokenId, index) => ({
        ...acc,
        [tokenId.toString()]: deposits[index]!,
      }),
      {} as Record<string, IStakedDeposit>,
    );
  }, [readDeposits.data]);

  return useMemo(
    () => ({
      ...readDeposits,
      depositsRecord,
    }),
    [readDeposits, depositsRecord],
  );
}

export interface IStakedDeposit {
  owner: Address;
  depositDate: number;
  lastRewardDate: number;
  bonusStartDate: number;
  bonusPerDay: bigint;
}

export function useDepositsOrThrow(queryOptions: { enabled?: boolean } = {}) {
  const depositsRead = useDeposits(queryOptions);
  if (!depositsRead.data) {
    throw new Error(
      'Usage of useDepositsOrThrow while the data is not fetched yet.',
    );
  }
  const [tokenIds, deposits] = depositsRead.data;

  return {
    tokenIds,
    deposits,
    depositsRecord: depositsRead.depositsRecord,
  };
}

export function useRewardsOf({ enabled }: { enabled?: boolean } = {}) {
  const depositsOf = useDeposits({ enabled });
  const tokenIds = depositsOf.data?.[0];

  const readRewards = useReadContracts({
    contracts: tokenIds?.map(
      (tokenId) =>
        ({
          chainId: chains.base.id,
          address: env.base.STAKING_ADDRESS,
          abi: STAKING_CONTRACT_ABI,
          functionName: 'rewardsOf',
          args: [tokenId],
        }) as const,
    ),
    query: {
      enabled: enabled && depositsOf.isSuccess,
      select: (data) =>
        data.reduce(
          (acc, reward, index) => {
            if (reward.status === 'failure') {
              return acc;
            }

            const [base, bonus] = reward.result;
            return {
              ...acc,
              [tokenIds![index]!.toString()]: {
                base,
                bonus,
              },
            };
          },
          {} as Record<string, { base: bigint; bonus: bigint }>,
        ),
    },
  });

  useWatchBlockNumber({
    onBlockNumber: (blockNumber) => {
      if (blockNumber % 3n === 0n) {
        readRewards.refetch({ cancelRefetch: false });
      }
    },
    enabled: enabled && depositsOf.isSuccess,
  });

  return readRewards;
}

export function useRewardsOfOrThrow(queryOptions: { enabled?: boolean } = {}) {
  const rewardsRead = useRewardsOf(queryOptions);
  if (!rewardsRead.data) {
    throw new Error(
      'Usage of useRewardsOfOrThrow while the data is not fetched yet.',
    );
  }
  return rewardsRead.data;
}

export function useTotalRewardsOrThrow() {
  const rewardsOf = useRewardsOfOrThrow();
  return Object.values(rewardsOf).reduce(
    (acc, { base, bonus }) => acc + base + bonus,
    0n,
  );
}

export function useClaimAll() {
  const t = useTranslations();
  const mutation = useMutation({
    mutationFn: claim,
    meta: {
      notifyError: true,
    },
  });
  const receipt = useWaitForTransactionReceipt({
    hash: mutation.data,
    timeout: READ_RECEIPT_TIMEOUT,
    query: {
      meta: {
        notifyError: true,
        notifySuccess: {
          t,
          translationKey: 'REWARDS_ACTION_SUCCESS',
          translationValues: (data: WaitForTransactionReceiptReturnType) => ({
            rewards: Number(formatEther(getClaimedReward(data.logs))).toFixed(
              2,
            ),
          }),
        },
      },
    },
  });
  return {
    mutation,
    receipt,
    isLoading: mutation.isPending || receipt.isLoading,
  };
}

export function useStakeAll() {
  const t = useTranslations();
  const mutation = useMutation({
    mutationFn: stakeOnBase,
    meta: {
      notifyError: true,
    },
  });
  const receipt = useWaitForTransactionReceipt({
    hash: mutation.data,
    timeout: READ_RECEIPT_TIMEOUT,
    query: {
      meta: {
        notifyError: true,
        notifySuccess: {
          t,
          translationKey: 'STAKE_ALL_ACTION_SUCCESS',
        },
      },
    },
  });
  return {
    mutation,
    receipt,
    isLoading: mutation.isPending || receipt.isLoading,
  };
}

export function useUnstakeAll() {
  const t = useTranslations();
  const mutation = useMutation({
    mutationFn: unstake,
    meta: {
      notifyError: true,
    },
  });
  const receipt = useWaitForTransactionReceipt({
    hash: mutation.data,
    timeout: READ_RECEIPT_TIMEOUT,
    query: {
      meta: {
        notifyError: true,
        notifySuccess: {
          t,
          translationKey: 'UNSTAKE_ALL_ACTION_SUCCESS',
          translationValues: (data: WaitForTransactionReceiptReturnType) => ({
            rewards: Number(formatEther(getClaimedReward(data.logs))).toFixed(
              2,
            ),
          }),
        },
      },
    },
  });
  return {
    mutation,
    receipt,
    isLoading: mutation.isPending || receipt.isLoading,
  };
}

export function useUnstakedPersonas(blockchain?: Blockchain) {
  const completedUser = useCurrentCompletedUser();
  const { tokenIds: stakedTokenIds } = useDepositsOrThrow();
  const wallet = useAccount();

  const personas = useMemo(() => {
    if (wallet.status !== 'connected' || !completedUser) return [];

    const currentUserWallet = wallet.address;
    return completedUser.user.personas.filter((persona) => {
      if (!isAddressEqual(persona.ownerAddress, currentUserWallet))
        return false;
      if (stakedTokenIds.includes(persona.tokenId)) return false;
      if (blockchain && persona.blockchain !== blockchain) return false;

      return true;
    });
  }, [completedUser?.user.personas, wallet, stakedTokenIds]);

  return { personas };
}

export function useUnstakedTokens(blockchain?: Blockchain) {
  const { personas } = useUnstakedPersonas(blockchain);

  return { tokenIds: personas.map(({ tokenId }) => tokenId) };
}

export function useApprovalForAll() {
  const t = useTranslations();
  const mutation = useMutation({
    mutationFn: async ({
      blockchain,
      operator,
    }: {
      blockchain: Blockchain;
      operator: Address;
    }) => {
      if (
        !(await hasApprovalForAllNfts({
          blockchain,
          operator,
        }))
      ) {
        return setApprovalForAllNfts({
          blockchain,
          operator,
        });
      }
    },
    meta: {
      notifyError: true,
    },
  });
  const receipt = useWaitForTransactionReceipt({
    hash: mutation.data,
    timeout: READ_RECEIPT_TIMEOUT,
    query: {
      meta: {
        notifyError: true,
        notifySuccess: {
          t,
          translationKey: 'BRIDGE_ACTION_SUCCESS',
        },
      },
    },
  });

  return {
    mutation,
    receipt,
    isLoading: mutation.isPending || receipt.isLoading,
  };
}

function useOwners({
  blockchain,
  tokenIds,
}: {
  blockchain: Blockchain;
  tokenIds: ReadonlyArray<IPersonaFront['tokenId']>;
}) {
  const readOwners = useReadContracts({
    contracts: tokenIds.map(
      (tokenId) =>
        ({
          chainId: chains[blockchain].id,
          address: env[blockchain].PERSONA_ADDRESS,
          abi: PERSONA_CONTRACT_ABI,
          functionName: 'ownerOf',
          args: [tokenId],
        }) as const,
    ),
    query: {
      enabled: tokenIds.length > 0,
    },
  });

  useWatchBlockNumber({
    onBlockNumber: (blockNumber) => {
      if (blockNumber % 3n === 0n) {
        readOwners.refetch({ cancelRefetch: false });
      }
    },
  });

  const ownersRecord = useMemo(
    () =>
      readOwners.data?.reduce<Record<string, Address>>(
        (acc, { result }, index) => ({
          ...acc,
          [tokenIds[index]!.toString()]: result ?? zeroAddress,
        }),
        {},
      ) ?? {},
    [readOwners.data],
  );

  return useMemo(
    () => ({
      ...readOwners,
      ownersRecord,
    }),
    [readOwners, ownersRecord],
  );
}

export function useBridgeStatus({
  destination,
  tokenIds,
}: {
  destination: Blockchain;
  tokenIds: ReadonlyArray<IPersonaFront['tokenId']>;
}) {
  const bridgeAddress =
    destination === Blockchain.ETHEREUM
      ? env.ethereum.BRIDGE_ADDRESS
      : zeroAddress;
  const { ownersRecord } = useOwners({ blockchain: destination, tokenIds });
  const owners = Object.values(ownersRecord);

  if (!tokenIds.length) {
    return 'success' as const;
  }

  if (!owners.length) {
    return 'pending' as const;
  }

  if (owners.some((owner) => isAddressEqual(owner, bridgeAddress))) {
    return 'pending' as const;
  }

  return 'success' as const;
}

export function usePersonaRewards(
  tokenIds: readonly bigint[],
  queryOptions: { enabled?: boolean } = {},
) {
  const rewardItems = useRewardsOfOrThrow(queryOptions);

  return tokenIds.reduce((acc, tokenId) => {
    const reward = rewardItems[tokenId.toString()];
    if (!reward) return acc;
    return acc + reward.base + reward.bonus;
  }, 0n);
}

export function usePersonaOwnerMatches(persona: IPersonaFront) {
  const personas = useCurrentUser().user?.personas ?? [];
  const { ownersRecord: ethereumOwners } = useOwners({
    blockchain: Blockchain.ETHEREUM,
    tokenIds: personas
      .filter((persona) => persona.blockchain === Blockchain.ETHEREUM)
      .map((persona) => persona.tokenId),
  });
  const { ownersRecord: baseOwners } = useOwners({
    blockchain: Blockchain.BASE,
    tokenIds: personas
      .filter((persona) => persona.blockchain === Blockchain.BASE)
      .map((persona) => persona.tokenId),
  });
  const tokenId = persona.tokenId.toString();
  const currentOwner = ethereumOwners[tokenId] ?? baseOwners[tokenId];

  if (!personas.length || !currentOwner) {
    // Data not yet ready, assume persona is synced.
    return true;
  }

  return isAddressEqual(persona.ownerAddress, currentOwner);
}
